import { withStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import { CircularProgress } from "@mui/material";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { FaAsterisk, FaCheckCircle, FaExclamationCircle, FaRegCheckCircle, FaRegCircle, FaTrashAlt } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { saveAttachments } from "../../../../redux/actions/lenderRequestActions";
import { DocumentUpload } from "../../Shared/DocumentUpload";
import { uploadFile } from "../../../shared/services/FileUploadService";
import { isOverflowingX } from "../../../shared/utilities/isOverflowing";
import { REQUEST_STATUSES } from "../../../shared/constants/RequestStatuses";
import { REQUEST_TYPES } from "../../../shared/constants/RequestTypes";

import "../../../AddNewProject/AddNewProject.css";
import "../../../AddNewProject/Pages/AttachmentsPage.css";

// TODO: Confirm this is correct
const techSupportUrl = "https://singlefamily.fanniemae.com/applications-technology/technology-support-center";

const sx = {
  tooltip: {
    color: "black",
    backgroundColor: "white",
    border: "1px solid #E6E8ED",
    fontSize: 14,
  },
  arrow: {
    color: "white",
    "&:before": {
      border: "1px solid #E6E8ED",
    },
  }
};

const StyledTooltip = withStyles(sx)(Tooltip);

// Show tooltip only if text is truncated
const CustomTooltip = (props) => {
  return <StyledTooltip
    {...props}
    disableFocusListener
    disableTouchListener
    disableHoverListener={props.target && !isOverflowingX(props.target)}
  />;
};

// Attachments page for adding additional documents to a request that has been sent back for more documentation. 
const AttachmentsPage = ({ incrementCounter, decrementCounter, request, requirements, optional }) => {  
  const [showValidation, setShowValidation] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(false);
  
  const dispatch = useDispatch(); 
  const {requestId, projectId, requestTypeName} = request;

  const [phaseId, setPhaseId] = useState('');
  const [phaseName, setPhaseName] = useState('');

  const [requiredDropdown, setRequiredDropdown] = useState([]);
  const [optionalDropdown, setOptionalDropdown] = useState([]);
  const [unfilledRequirements, setUnfilledRequirements] = useState();
  const [isValid, setIsValid] = useState(false);


  useEffect(() => {
    if (requestTypeName === REQUEST_TYPES.LENDER_EDIT_PHASE && requestId) {
      axios.get(
        `${global.REACT_APP_API_ENDPOINT}/lender/request/projectStagingData?masterTrackerId=${requestId}`
      ).then(response => {
        if (response.status === 200) {
          setPhaseId(response.data?.phaseId ?? requestPhaseId);
          setPhaseName(response.data?.phaseName ?? request.phaseName);
        }
      }).catch(error => {
        console.error("Error fetching staging data for phaseId/phaseName:", error);
      });
    }
  }, [requestId, requestTypeName]);

  useEffect(() => {
    if (optional && requirements && uploads) {
      if (!requirements.flat().length && !optional?.length) {
        // should be impossible
        console.log('No required documents and no optional documents, skipping to next step');

        incrementCounter();

        return;
      }

      const addedDocTypes = new Set(uploads.map(fileObj => fileObj?.docType?.documentRankingId));
      const remainingNested = requirements.map(reqSet => 
        reqSet.some(req => addedDocTypes.has(req.documentRankingId))
          ? [] 
          : reqSet
      );
      const fulfilledNested = requirements.map(reqSet => 
        reqSet.some(req => addedDocTypes.has(req.documentRankingId))
          ? reqSet
          : []
      );

      const remainingFlat = new Set(remainingNested.flat());
      const fulfilled = new Set(fulfilledNested.flat());
      const fulfilledOrOptional = new Set([ ...fulfilled, ...optional.flat() ]);

      // console.log('requirements updated', {requirements, optional, uploads, addedDocTypes, remainingNested, fulfilledNested, remainingFlat, fulfilledOrOptional});

      // setDocSubTypeLookup(subTypeLookup);      
      setRequiredDropdown([ ...remainingFlat ]);
      setOptionalDropdown([ ...fulfilledOrOptional ]);
      setUnfilledRequirements(remainingNested);
      setUploadFailed(false);
    }    
  }, [optional, requirements, uploads]);

  useEffect(() => {
    if (unfilledRequirements && requiredDropdown) {
      setIsValid(!requiredDropdown.length && uploads.length > 0);
    }
  }, [unfilledRequirements, requiredDropdown, uploads])

  function handleAddFile(fileObj) {
    const files = [ ...uploads ];    
    files.push(fileObj);
    setUploads(files);
    // console.log('handleAddFile', {fileObj, files, uploads})
  }

  function handleRemoveFile(index) {
    const files = [ ...uploads ];    
    files.splice(index, 1);
    setUploads(files);
    setShowValidation(true);
    // console.log('handleRemoveFile', {index, files, uploads})
  }

  function handleBackButton(e) {
    e.preventDefault();    
    history.back();
  }

  async function handleSubmit(e) {
    e.preventDefault();    

    setUploadFailed(false);
    setShowValidation(true);
    
    if (isValid) {
      setUploading(true);

      // console.log('Submitting attachments', {requestId, projectId, phaseId, uploads})

      const uploadResults = [];

      const uploadTasks = uploads.map((fileObj, i) => {
        let resolve, reject;

        const promise = new Promise((_resolve, _reject) => {
          resolve = _resolve;
          reject = _reject;
        });

        const onSuccess = (data) => {
          // console.log('upload success callback ' + i, {i, data, fileObj})
          const result = {
            documentRankingId: fileObj.docType.documentRankingId,
            docMapId: data?.docMapId,
            note: fileObj.note
          };
          uploadResults.push(result);
          resolve(result);
        };   

        const onFail = (error) => {
          console.error('upload error callback ' + i, {i, error, fileObj});
          reject(error);
        };

        uploadFile(
          fileObj.file, 
          fileObj.note, 
          null, 
          phaseId,
          projectId,
          fileObj.file.name,
          onSuccess, 
          onFail
        )
        .catch(onFail);      
        
        return promise;
      });

      const results = await Promise.allSettled(uploadTasks);
      const succeeded = results.filter(result => result.status === "fulfilled");
      const failed = results.filter(result => result.status === "rejected");

      console.log('all uploads complete', {uploadResults, results, succeeded, failed})

      if (succeeded.length === results.length) {
        const result = await dispatch(saveAttachments(requestId, uploadResults, incrementCounter));
        setUploadFailed(!result);

        if (result && request.statusName === REQUEST_STATUSES.ADDITIONAL_INFORMATION_REQUIRED) {
          submitNewStatus();
        }
      } else {
        console.error('Some uploads failed', {results, succeeded, failed})
        setUploadFailed(true);
      }
      
      setUploading(false);
    }
  }

  // todo: convert to redux action
  function submitNewStatus() {
    const payload = {
      // requestActivityTrackerId: null,
      requestMasterTrackerId: request.requestId,
      assignTo: request.assignedToLdapId,
      createdBy: request.assignedToLdapId,
      notes: "Additional documents received.",
      isEscalated: null,
      notesIsExternal: true,
      activity: "status Changed",
      statusName: REQUEST_STATUSES.ADDITIONAL_INFORMATION_RECEIVED
    };

    axios.post(
      global.REACT_APP_API_ENDPOINT + `/lender/request/saveActivityTrackerWithAdditionalInfoCheck`,
      payload
    ).then((response) => {
      console.log("Status updated:", response.data);
    }).catch((error) => {
      console.error("Error updating status:", error);
    });
  }

  function describeRequirement(reqSet) {
    // console.log('describeRequirement', reqSet)
    const labels = reqSet.map(req => req.name);

    return reqSet.length === 1
      ? labels[0]
      : reqSet.length === 2
      ? `${labels[0]} and/or ${labels[1]}`
      : `At least one of ${labels.slice(0, -1).join(", ")}, and/or ${labels[labels.length - 1]}`;
  }

  function getBullet(index) {
    // all missing required docTypes in this subset, if any
    const missingDocs = unfilledRequirements[index];

    return missingDocs && !missingDocs.length // requirement fulfilled
      ? <FaCheckCircle className="req-bullet completed" />
      : !showValidation || !missingDocs  // validation not triggered or list not initialized
      ? <FaRegCircle className="req-bullet default" />
      : <FaExclamationCircle className="req-bullet error" />
  }

  const fileNameRefs = useRef([]);
  const fileNoteRefs = useRef([]);
  const fileDocTypeRefs = useRef([]);

  return (
    <form className={"upload-docs-form " + (showValidation ? "show-validation" : "")}>
      <section className="title-section">
        <h1>{requestTypeName || "Lender"} Request</h1>
        <h6>Section 1 of 2 - Upload Documents</h6>
      </section>
    
      {unfilledRequirements ? (
        <section className="main-section">
          {request.statusName === REQUEST_STATUSES.ADDITIONAL_INFORMATION_REQUIRED && requiredDropdown.length ? (
            <>
              <p>Please upload the following documents:</p>
              <ul className="req-list">
                {requirements.map((reqSet, index) => 
                  <li key={index}>
                    {getBullet(index)}
                    <div className="req-desc">
                      {describeRequirement(reqSet)}
                    </div>
                  </li>
                )}
              </ul>
            </>
          ) : (
            <p>Please upload any additional documents relevant to your request.</p>
          )}
          {uploads.length ? ( 
            <div className="uploaded-files-list">
              <div className="uploaded-files-row header-row">
                <div className="file-doctype">Document Type</div>
                <div className="file-status-bar">File</div>             
                <div className="file-note">Note</div>
              </div>
              {uploads.map((fileObj, index) => (
                <div key={index} className="uploaded-files-row">
                  <CustomTooltip 
                    target={fileDocTypeRefs.current[index]} 
                    title={fileObj.docType.name} 
                    placement="top"
                  >
                    <div ref={ref => fileDocTypeRefs.current[index] = ref} className="file-doctype">
                      {fileObj.docType.name}
                    </div>
                  </CustomTooltip>

                  <div className="file-status-bar">
                    <CustomTooltip target={fileNameRefs.current[index]} title={fileObj.file.name} placement="top">
                      <div ref={ref => fileNameRefs.current[index] = ref} className="file-name">
                        {fileObj.file.name}
                      </div>
                    </CustomTooltip>
                    <div className="file-remove" onClick={() => handleRemoveFile(index)}>
                      <FaTrashAlt className="delete-button" />
                    </div>
                  </div>        

                  <CustomTooltip 
                    target={fileNoteRefs.current[index]} 
                    title={fileObj.note} 
                    placement="top"
                  >
                    <div ref={ref => fileNoteRefs.current[index] = ref} className="file-note">
                      {fileObj.note ? fileObj.note : (
                        <span className="empty-field">--</span>
                      )}
                    </div>
                  </CustomTooltip>
                </div>
              ))}
            </div>
          ) : (<></>)}
          <DocumentUpload
            requiredList={requiredDropdown} 
            optionalList={optionalDropdown} 
            onAddFile={fileObj => handleAddFile(fileObj)}
            />
        </section>
      ) : (<></>)}

      <div className="stepper-controls" style={{marginTop: "40px"}}>
        <div className="control-cell">
          <Button
            type="submit"
            className="back-button"
            onClick={handleBackButton}
            form="addProjectForm"
          >
            Back
          </Button>
        </div>
        <div className="control-cell">
          <Button
            type="submit"
            className={"btn-submit" + 
              (!isValid ? " btn-invalid" : "") +
              (uploading ? " btn-loading" : "")
            }
            disabled={uploading}
            onSubmit={handleSubmit}
            onClick={handleSubmit}
            form="addProjectForm"
          >
            {uploading ? (
              <CircularProgress
                style={{
                  display: "inline-flex",
                  padding: "10px",
                  color: "#fff",
                  justifySelf: "center",
                  alignSelf: "center"
                }}
              />
            ) : ("Upload Files")}              
          </Button>
        </div>
      </div>

      {showValidation && !uploads.length ? (
        <div className="upload-error-message">
          <div>
            Please upload at least one document before submitting.
          </div>
        </div>
      ) : (<></>)}

      {uploadFailed ? (
        <div className="upload-error-message">
          <div>
            Failed to upload documents. Please try again. If the problem persists, 
            please <a href={techSupportUrl}>contact Fannie Mae technology support.</a>
          </div>
        </div>
      ) : (<></>)}
    </form>
  );
};

export default AttachmentsPage;
